import React, { useState } from 'react';
import { Box, Chip, Grid, Link, Typography } from '@mui/material';
import { useL2DTheme } from '@europrocurement/flexy-components';
import { useDomainStatusStyle, DOMAIN_STATUS_CHIP_IDS } from '@europrocurement/l2d-modules';
import { useDomainesCtx } from '@europrocurement/l2d-modules/providers/DomainesProvider';
import { Tiers } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { DomainCard } from '../DomainCard';

type DomainsSectionProps = {
    prescripteur: Tiers;
};

const PAGE_SIZE = 6;

const DomainesSection: React.FunctionComponent<DomainsSectionProps> = function ({ prescripteur }) {
    const { palette } = useL2DTheme();
    const [showAll, setShowAll] = useState(false);
    const { getDomainStatusStyle, getDomainStatusLabel } = useDomainStatusStyle();
    const { getDomaines } = useDomainesCtx();
    const globalDomains = getDomaines();
    const prescriberDomains = prescripteur.domaines ?? [];
    const domainStatutMap = new Map(prescriberDomains.map((d) => [d.id, d]));

    const domainsToDisplay = (
        globalDomains.length > 0
            ? globalDomains.map((gd) => {
                  const pd = domainStatutMap.get(gd.id);
                  return {
                      id: gd.id,
                      libelle: gd.libelle,
                      statut: pd?.statut,
                      dateDebut: pd?.dateDebut,
                  };
              })
            : prescriberDomains.map((pd) => ({
                  id: pd.id,
                  libelle: pd.libelle ?? `Domaine ${pd.id}`,
                  statut: pd.statut,
                  dateDebut: pd.dateDebut,
              }))
    ).sort((a, b) => {
        const aActive = a.statut !== undefined;
        const bActive = b.statut !== undefined;
        if (aActive !== bActive) return aActive ? -1 : 1;
        return a.libelle.localeCompare(b.libelle, 'fr');
    });

    const countByStatut = Object.fromEntries(
        DOMAIN_STATUS_CHIP_IDS.map((id) => [
            id,
            domainsToDisplay.filter((d) => d.statut?.id === id).length,
        ]),
    );

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 0.5,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: palette.text.secondary,
                    }}
                >
                    Accès aux services par domaine
                </Typography>
                {DOMAIN_STATUS_CHIP_IDS.map((id) => {
                    const count = countByStatut[id];
                    if (!count) return null;
                    const { chipSx } = getDomainStatusStyle(id);
                    const label = getDomainStatusLabel(id, count);
                    if (!label) return null;

                    return (
                        <Chip
                            key={id}
                            label={label}
                            size="small"
                            sx={{ height: 18, fontSize: '10px', borderRadius: '0.5em', ...chipSx }}
                        />
                    );
                })}
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.75em', color: palette.text.gris35 }}>
                    Le prescripteur peut envoyer des demandes par e-mail pour chaque domaine, en
                    complément de l&lsquo;accès plateforme.
                </Typography>
            </Box>
            <Grid
                container
                columns={PAGE_SIZE}
                spacing={1.5}
                mt={1}
            >
                {(showAll ? domainsToDisplay : domainsToDisplay.slice(0, PAGE_SIZE)).map(
                    (domain) => (
                        <Grid
                            key={domain.id}
                            item
                            xs={1}
                            sx={{ display: 'flex' }}
                        >
                            <DomainCard
                                domainName={domain.libelle}
                                statutId={domain.statut?.id}
                                statutLibelle={domain.statut?.libelle}
                                dateDebut={domain.dateDebut}
                            />
                        </Grid>
                    ),
                )}
            </Grid>
            {domainsToDisplay.length > PAGE_SIZE && (
                <Link
                    component="button"
                    variant="caption"
                    onClick={() => setShowAll((prev) => !prev)}
                    sx={{ mt: 1, display: 'block', textAlign: 'right', width: '100%' }}
                >
                    {showAll
                        ? 'Afficher moins'
                        : `Afficher les ${domainsToDisplay.length - PAGE_SIZE} autres domaines`}
                </Link>
            )}
        </Box>
    );
};

export default DomainesSection;
